import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Agent, AgentStateTypes, SalesReport } from "./Agent.util";

const name = "agent";
const extraActions = createExtraActions();

const AgentInitialState: AgentStateTypes = {
  createAgent: ResetApiState(null),
  resetCreateAgent: ResetApiState(null),
  editAgent: ResetApiState(null),
  resetEditAgent: ResetApiState(null),
  deleteAgent: ResetApiState(null),
  resetDeleteAgent: ResetApiState(null),
  resetPassword: ResetApiState(null),
  resetResetPassword: ResetApiState(null),
  agents: ResetApiState([]),
  salesReport: ResetApiState([]),
  pagedAgents: ResetApiState(ResetPaginationState()),
  agent: ResetApiState(null),
  resetAgent: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createAgent = createAsyncThunk<ResponseWrapper<Agent>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateAgent = createAction(`/reset-create-${name}`);

  const editAgent = createAsyncThunk<ResponseWrapper<Agent>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditAgent = createAction(`/reset-edit-${name}`);

  const deleteAgent = createAsyncThunk<
    ResponseWrapper<{
      raw: any[];
      affected: number;
    }>,
    any
  >(`/delete-${name}`, async (id: any, thunkApi) =>
    axios
      .delete(`${name}/${id}`)
      .then((response) => {
        if (response.data.success) {
          return id;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error?.response?.data);
      })
  );

  const resetDeleteAgent = createAction(`/reset-delete-${name}`);

  const resetPassword = createAsyncThunk<
    ResponseWrapper<{
      raw: any[];
      affected: number;
    }>,
    any
  >(`/rest-${name}-password`, async (id: any, thunkApi) =>
    axios
      .post(`${name}/reset/${id}`)
      .then((response) => {
        if (response.data.success) {
          return id;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error?.response?.data);
      })
  );

  const resetResetPassword = createAction(`/reset-rest-${name}-password`);

  const getAgents = createAsyncThunk<Agent[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getSalesReport = createAsyncThunk<SalesReport[], any>(
    `/get-sales-report`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/sales-report/${query?.startDate}/${query?.endDate}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedAgents = createAsyncThunk<
    ResponseWrapper<PaginationType<Agent[]>>,
    any
  >(`/get-paged-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/paged${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getAgent = createAsyncThunk<ResponseWrapper<Agent>, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetAgent = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createAgent,
    resetCreateAgent,
    editAgent,
    resetEditAgent,
    deleteAgent,
    resetDeleteAgent,
    resetPassword,
    resetResetPassword,
    getAgents,
    getSalesReport,
    getPagedAgents,
    getAgent,
    resetAgent,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<AgentStateTypes>
) {
  return {
    ...createAgentReducer(),
    ...resetCreateAgent(),
    ...editAgentReducer(),
    ...resetEditAgent(),
    ...deleteAgentReducer(),
    ...resetDeleteAgent(),
    ...resetPasswordReducer(),
    ...resetResetPasswordAgent(),
    ...agentsReducer(),
    ...salesReportReducer(),
    ...pagedAgentsReducer(),
    ...agentReducer(),
    ...resetAgent(),
    ...resetStatesReducer(),
  };

  function createAgentReducer() {
    return {
      ...builder.addCase(extraActions.createAgent.pending, (state, action) => {
        state.createAgent = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.createAgent.fulfilled,
        (state, action) => {
          state.createAgent = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.createAgent.rejected, (state, action) => {
        state.createAgent = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCreateAgent() {
    return {
      ...builder.addCase(extraActions.resetCreateAgent, (state, action) => {
        state.resetCreateAgent = ResetApiState(true);
        state.createAgent = ResetApiState(null);
      }),
    };
  }

  function editAgentReducer() {
    return {
      ...builder.addCase(extraActions.editAgent.pending, (state, action) => {
        state.editAgent = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editAgent.fulfilled, (state, action) => {
        state.editAgent = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editAgent.rejected, (state, action) => {
        state.editAgent = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditAgent() {
    return {
      ...builder.addCase(extraActions.resetEditAgent, (state, action) => {
        state.resetEditAgent = ResetApiState(true);
        state.editAgent = ResetApiState(null);
      }),
    };
  }

  function deleteAgentReducer() {
    return {
      ...builder.addCase(extraActions.deleteAgent.pending, (state, action) => {
        state.deleteAgent = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.deleteAgent.fulfilled,
        (state, action) => {
          state.deleteAgent = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.deleteAgent.rejected, (state, action) => {
        state.deleteAgent = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetDeleteAgent() {
    return {
      ...builder.addCase(extraActions.resetDeleteAgent, (state, action) => {
        state.resetDeleteAgent = ResetApiState(true);
        state.deleteAgent = ResetApiState(null);
      }),
    };
  }

  function resetPasswordReducer() {
    return {
      ...builder.addCase(
        extraActions.resetPassword.pending,
        (state, action) => {
          state.resetPassword = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.resetPassword.fulfilled,
        (state, action) => {
          state.resetPassword = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.resetPassword.rejected,
        (state, action) => {
          state.resetPassword = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetResetPasswordAgent() {
    return {
      ...builder.addCase(extraActions.resetResetPassword, (state, action) => {
        state.resetResetPassword = ResetApiState(true);
        state.resetPassword = ResetApiState(null);
      }),
    };
  }

  function agentsReducer() {
    return {
      ...builder.addCase(extraActions.getAgents.pending, (state, action) => {
        state.agents = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAgents.fulfilled, (state, action) => {
        state.agents = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAgents.rejected, (state, action) => {
        state.agents = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedAgentsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedAgents.pending,
        (state, action) => {
          state.pagedAgents = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAgents.fulfilled,
        (state, action) => {
          state.pagedAgents = {
            loading: false,
            payload: action.payload.data,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAgents.rejected,
        (state, action) => {
          state.pagedAgents = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function agentReducer() {
    return {
      ...builder.addCase(extraActions.getAgent.pending, (state, action) => {
        state.agent = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAgent.fulfilled, (state, action) => {
        state.agent = {
          loading: false,
          payload: action.payload.data,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAgent.rejected, (state, action) => {
        state.agent = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function salesReportReducer() {
    return {
      ...builder.addCase(extraActions.getSalesReport.pending, (state, action) => {
        state.salesReport = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSalesReport.fulfilled, (state, action) => {
        state.salesReport = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSalesReport.rejected, (state, action) => {
        state.salesReport = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetAgent() {
    return {
      ...builder.addCase(extraActions.resetAgent, (state, action) => {
        state.resetAgent = ResetApiState(true);
        state.agent = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createAgent = ResetApiState(null);
        state.resetCreateAgent = ResetApiState(null);
        state.editAgent = ResetApiState(null);
        state.resetEditAgent = ResetApiState(null);
        state.deleteAgent = ResetApiState(null);
        state.resetDeleteAgent = ResetApiState(null);
        state.agents = ResetApiState([]);
        state.agent = ResetApiState(null);
        state.resetAgent = ResetApiState(null);
        state.resetAgent = ResetApiState(null);
      }),
    };
  }
}

const AgentSlice = createSlice({
  name,
  initialState: AgentInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const AgentActions = { ...AgentSlice.actions, ...extraActions };
export const AgentReducer = AgentSlice.reducer;
